//@ts-check
import React, { useCallback, useEffect, useRef, useState } from "react"
import PollContainer from "@components/poll/poll.container"
import useTeaserElement from "@components/content-feed/util/hooks/useTeaserElement";
import { BodyWrapper, BodyDescription, Panel } from "@components/Teaser/Panel"
import feedTeaserFactory from "./factory"
import withLegacyPropsMap from "@components/content-feed/util/hoc/withLegacyPropsMap";
import FeedTrivia from "./Trivia"
import useDetail from "@components/content-feed/util/hooks/useDetail";
import { VIEW_ACTION_DETAIL } from "./Link"

/**
 * @param {number} id
 * @param {array} answers
 * @param {number} [detailId]
 * @param {number} [userVote]
 * @return {[React.MutableRefObject<HTMLDivElement>, JSX.Element, string, (p: {id: number}) => void, number]}
 */
const usePollRevealTeaser = (id, answers, detailId, userVote = 0) => {
  const hadInitialVote = userVote > 0
  const [userVoteId, setUserVoteId] = useState(userVote)
  const handleVote = useCallback(({ id }) => setUserVoteId(id), [])
  const [teaserStatus, teaserElement] = useTeaserElement(
    ((answers || []).find(a => a.id === userVoteId) || {}).forward_id,
    ({ view_action, ...props }) => ({
      ...props,
      // Interactions with the teaser should be associated with the current slot
      id,
      // Loaded teasers cannot have detail pages (mibaby-plan#292)
      view_action: view_action === VIEW_ACTION_DETAIL ? null : view_action,
    })
  )
  const [detailStatus, detailHtml] = useDetail(userVoteId ? detailId : null)

  /** @type {React.MutableRefObject<HTMLDivElement>} */
  const scrollPaneRef = useRef()
  // when the loading status changes to loaded, scroll to the right!
  useEffect(() => {
    if (
      !hadInitialVote &&
      (detailStatus === "loaded" || teaserStatus === "loaded")
    ) {
      scrollPaneRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }
  }, [detailStatus, teaserStatus])

  return [
    scrollPaneRef,
    teaserStatus === "loaded" && teaserElement,
    detailHtml,
    handleVote,
    userVoteId,
  ]
}

/**
 * maps teaser props to poll props
 */
const ConnectedPoll = ({
  AGG_POLL,
  question_id: pollId,
  answers,
  handleVote,
  userOnly,
  registerLightboxId,
  pollRef,
}) => (
  <PollContainer
    {...AGG_POLL}
    ref={pollRef}
    pollId={pollId}
    answers={answers}
    handleVote={handleVote}
    userOnly={userOnly}
    registerLightboxId={registerLightboxId}
  />
)

/**
 * overriding body component to place poll inside
 */
const Body = ({ description, ...props }) => (
  <BodyWrapper>
    {description && <BodyDescription description={description} />}
    <ConnectedPoll {...props} />
  </BodyWrapper>
)

/**
 * wrapper uses the common teaser Wrapper (panel) and appends the
 * reveal teaser, if present
 */
const Wrapper = ({
  teaserStyle,
  scrollPaneRef,
  revealTeaser,
  revealType,
  detailHtml,
  children,
  detailTrivia,
}) => {
  let className = `content-feed-poll`

  if (revealTeaser || detailHtml) {
    className = `${className} content-feed-poll-revealed`
  }

  return (
    <>
      <div ref={scrollPaneRef} className={className}>
        <Panel
          teaserStyle={teaserStyle}
          className={`content-feed-poll-container`}
        >
          {children}
        </Panel>
      </div>
      {revealTeaser && (
        <>
          <div className="panel-path-connector panel-path-connector-default">
            <i className="fa fa-arrow-down" />
          </div>
          <div className="content-feed-poll-teaser">{revealTeaser}</div>
        </>
      )}
      {detailHtml && (
        <>
          <div className="panel-path-connector panel-path-connector-default">
            <i className="fa fa-arrow-down" />
          </div>
          <div
            className="content-feed-poll-row"
            dangerouslySetInnerHTML={{ __html: detailHtml }}
          />
        </>
      )}
    </>
  )
}

/**
 * composed teaser using the teaser api
 */
// @ts-ignore
const Teaser = feedTeaserFactory({
  displayName: "Poll",
  Body,
  Wrapper,
  Trivia: ({ userVoteId, ...props }) =>
    userVoteId ? <FeedTrivia {...props} /> : null,
})

/**
 * a HOC the manages the revealing state.
 */
const ConnectedTeaser = props => {
  const { AGG_POLL: { userVote } = { userVote: 0 } } = props
  const [
    pollRef,
    revealTeaser,
    detailHtml,
    handleVote,
    userVoteId,
  ] = usePollRevealTeaser(props.id, props.answers, props.detailId, userVote)

  if (!props.title && props.question) {
    props = { ...props, title: props.question }
  }

  return (
    <Teaser
      {...props}
      {...{ pollRef, revealTeaser, handleVote, detailHtml, userVoteId }}
    />
  )
}

export default withLegacyPropsMap(ConnectedTeaser)
